import { SiteFooter } from '@/modules/marketing/SiteFooter';
import { SiteHeader } from '@/modules/marketing/SiteHeader';

/**
 * The shell every marketing page outside the landing page uses.
 *
 * Same header, same footer, same container width, same gradient behind the hero, same centred
 * `max-w-3xl` column the landing page and the articles use. Six pages each rebuilding that is six
 * chances for one of them to drift into looking like a different site.
 *
 * `wide` exists for the index pages, whose content is a grid rather than prose and wants the full
 * six-xl width underneath a centred heading.
 */
export function MarketingPage({
  eyebrow,
  title,
  standfirst,
  children,
  wide = false,
}: {
  eyebrow: string;
  title: string;
  standfirst: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="bg-background min-h-screen">
      <SiteHeader />

      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="from-primary/10 pointer-events-none absolute inset-x-0 top-0 h-[32rem] bg-gradient-to-b to-transparent"
        />
        <div className="relative mx-auto max-w-6xl px-6 pt-16 pb-4 sm:pt-24">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-muted-foreground/80 text-xs font-medium tracking-[0.2em] uppercase">
              {eyebrow}
            </p>
            <h1 className="mt-6 text-3xl font-semibold tracking-tight text-balance sm:text-5xl">
              {title}
            </h1>
            <p className="text-muted-foreground mx-auto mt-5 max-w-xl text-base sm:text-lg">
              {standfirst}
            </p>
          </div>
        </div>
      </section>

      <main className={`mx-auto px-6 pb-24 ${wide ? 'max-w-6xl' : 'max-w-3xl'}`}>{children}</main>

      <SiteFooter />
    </div>
  );
}

/** A prose section: centred heading, left-aligned body, matching the articles. */
export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-16">
      <h2 className="text-center text-xl font-semibold tracking-tight text-balance sm:text-2xl">
        {title}
      </h2>
      <div className="text-muted-foreground mt-6 space-y-4 leading-relaxed">{children}</div>
    </section>
  );
}
